import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { ShopService } from '../../services/shop.service';
import { Shop } from '../../interfaces/shop.interface';
import { Router, ActivatedRoute } from '@angular/router';
import { ShopApiService } from '../../services/shop-api.service';
import { UpdateForm, NullUpdateForm } from '../../interfaces/auth.interface';
import { LoginData } from '../../interfaces/forms.interface';

import { AppComponent } from 'src/app/app.component';
import { UserSessionService } from '../../services/user-session.service';
import { environment } from 'src/environments/environment';
import {
  BUYER_DETAILS_RESPONSE_SHOPPING_CART_EXPIRED,
  BUYER_DETAILS_RESPONSE_SHOPPING_CART_CANCELED,
  BUYER_DETAILS_RESPONSE_ORDER_CREATED,
  BUYER_DETAILS_RESPONSE_NOT_FOUND,
} from 'src/app/consts';
import { UntypedFormGroup } from '@angular/forms';
import { User } from '../../interfaces/user.interface';
import { AuthService } from '../../services/auth.service';
import { DialogConfig } from '@angular/cdk/dialog';
import { CaptchaService } from '../../services/captcha.service';
import { OrderSummaryService } from '../../services/order-summary.service';
import { LocaleService } from '../../services/locale.service';
import { filter, first, Subject, takeUntil } from 'rxjs';
import { isPlatformServer } from '@angular/common';
import * as Sentry from '@sentry/angular';

@Component({
  selector: 'app-details',
  templateUrl: './details.component.html',
  styleUrls: ['./details.component.scss'],
})
export class DetailsComponent implements OnInit, OnDestroy {
  shop: Shop;
  assetsDir: string = '';
  appicLoginExpand = true;
  showAppicNewAccount = false;
  showAppicUpdateForm = false;
  showNullUpdateForm = false;
  updateForm: UpdateForm;
  nullUpdateForm: NullUpdateForm;
  hideOrderBottom = true;
  loginData: LoginData;
  nameShop: string;
  detailsForm: UntypedFormGroup;
  destroy$ = new Subject();
  constructor(
    public shopService: ShopService,
    private shopApiService: ShopApiService,
    private router: Router,
    private route: ActivatedRoute,
    private appComponent: AppComponent,
    private userSessionService: UserSessionService,
    private localeService: LocaleService,
    @Inject(PLATFORM_ID) private platformId: object,
    private authService: AuthService,
    private captchaService: CaptchaService,
    private orderSummaryService: OrderSummaryService
  ) {}

  ngOnInit(): void {
    if (isPlatformServer(this.platformId)) return;
    this.orderSummaryService.nextStepClicked$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.submitManualInfo();
    });

    this.orderSummaryService.options = {
      nextStep: 'payment',
      nextButton: true,
      nextButtonDisabled: true,
    };
    this.captchaService.setCaptchaVisibility('visible');
    this.shop = this.shopService.shop;

    if (environment.production) {
      this.assetsDir = environment.deploy_url;
    }

    if (this.userSessionService.user?.['profile']) {
      this.nextStep();
    }

    this.route.queryParams.subscribe((params) => {
      this.nameShop = params['name'];

      if (params['transaction']) {
        this.getShoppingCart(params['transaction']);
        if (
          params['update_form'] &&
          (params['email'] ||
            params['gender'] ||
            params['first_name'] ||
            params['last_name'] ||
            params['city'] ||
            params['country'] ||
            params['birth_date'])
        ) {
          this.updateForm = {
            email: params['email'],
            gender: params['gender'],
            first_name: params['first_name'],
            last_name: params['last_name'],
            city: params['city'],
            country: params['country'],
            birth_date: params['birth_date'],
          };
          this.showAppicUpdateForm = true;
        } else if (
          params['null_form'] &&
          (params['gender'] || params['first_name'] || params['last_name'] || params['city'] || params['country'] || params['birth_date'])
        ) {
          this.nullUpdateForm = {
            gender: params['gender'],
            first_name: params['first_name'],
            last_name: params['last_name'],
            city: params['city'],
            country: params['country'],
            birth_date: params['birth_date'],
          };
          this.showNullUpdateForm = true;
        } else if (params['called_facebook_login'] === 'true') {
          this.appComponent.openDialog('application_errors.facebook_login_rejected');
        }
      } else {
        this.router.navigate(['../../'], { relativeTo: this.route });
      }
    });
  }

  submitManualInfo() {
    const user: User = this.detailsForm.value;

    this.shopApiService.putManualDetails(this.shopService.shoppingCart.uuid, user).subscribe(
      () => {
        this.userSessionService.user = user;
        sessionStorage.setItem(
          'formData',
          JSON.stringify({ ...this.detailsForm.getRawValue() /* , birth_date: this.detailsForm.get('birth_date').value.toString() */ })
        );

        this.router.navigate([`../payment`], {
          relativeTo: this.route,
          queryParamsHandling: 'preserve',
        });
      },
      (error) => {
        if ('error' in error.error.error) {
          const errorContent = error.error.error.error;
          if (errorContent.type === BUYER_DETAILS_RESPONSE_SHOPPING_CART_EXPIRED) {
            this.appComponent.openDialog('application_errors.shopping_cart_expired', this.navigateOnCallback);
          } else if (errorContent.type === BUYER_DETAILS_RESPONSE_SHOPPING_CART_CANCELED) {
            this.appComponent.openDialog('application_errors.shopping_cart_canceled', this.navigateOnCallback);
          } else if (errorContent.type === BUYER_DETAILS_RESPONSE_ORDER_CREATED) {
            this.appComponent.openDialog('application_errors.order_for_this_shopping_cart_already_created', this.navigateOnCallback);
          } else if (errorContent.type === BUYER_DETAILS_RESPONSE_NOT_FOUND) {
            this.appComponent.openDialog('application_errors.shopping_cart_not_found', this.navigateOnCallback);
          } else {
            Sentry.captureException(error.error || error, {
              tags: {
                feature: 'manual-details',
                module: 'details',
              },
            });
          }
        }
      }
    );
  }

  navigateOnCallback(callback: Function) {
    if (callback) {
      this.router.navigate(['../tickets'], {
        relativeTo: this.route,
      });
    }
  }

  getShoppingCart(shopping_cart_uuid: string) {
    const errCb = () => this.router.navigate(['../tickets'], { relativeTo: this.route });
    this.shopService
      .initShoppingCart(shopping_cart_uuid, errCb)
      .pipe(
        filter((val) => !!val),
        first()
      )
      .subscribe((shoppingCart) => {
        if (shoppingCart.status !== 'active' || shoppingCart.shop_uuid !== this.shop.uuid) {
          this.router.navigate(['../tickets'], { relativeTo: this.route });
        }
      });
  }

  nextStep() {
    this.router.navigate([`../payment`], {
      relativeTo: this.route,
      queryParamsHandling: 'preserve',
    });
  }

  back() {
    this.router.navigate([`../tickets`], {
      relativeTo: this.route,
      queryParamsHandling: 'preserve',
    });
  }

  setLoginData(loginData: LoginData) {
    this.loginData = loginData;
  }

  openLoginDialog() {
    const dialogConfig: DialogConfig = {
      data: { redirectToPayment: true },
    };
    this.authService.openAuthDialog(dialogConfig);
  }

  setFacebookRedirect(param) {
    this.shopService.facebookRedirect = param;
  }

  ngOnDestroy(): void {
    this.captchaService.setCaptchaVisibility('hidden');
    this.destroy$.next(true);
  }
}
